import { ReactNode, useState, useRef, useEffect } from "react";

interface ActionSheetProps {
  children: ReactNode;
  heightPercent?: ActionSheetHeightPercent; // Optional prop to set initial height percentage (default to 40%)
  setHeightPercent?: (height: ActionSheetHeightPercent) => void; // Optional callback to notify height changes
}

export type ActionSheetHeightPercent = 15 | 50 | 85;
export type ActionSheetState = "collapsed" | "half" | "expanded"; // Corresponding states
const heightOptions: Record<ActionSheetState, ActionSheetHeightPercent> = {
  collapsed: 15,
  half: 50,
  expanded: 85,
};

export function ActionSheet({
  children,
  heightPercent = 50,
  setHeightPercent,
}: ActionSheetProps) {
  const [expandedState, setExpandedState] = useState<ActionSheetState>("half");
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentHeight, setCurrentHeight] = useState(50); // Start at 40% height
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;

      e.preventDefault();
      const touch = e.touches[0];
      const deltaY = startY - touch.clientY;
      const viewportHeight = window.innerHeight;
      const deltaPercent = (deltaY / viewportHeight) * 100;

      const newHeight = Math.max(
        heightOptions.collapsed,
        Math.min(heightOptions.expanded, currentHeight + deltaPercent)
      );
      setCurrentHeight(newHeight);
    };

    const handleTouchEnd = () => {
      if (!isDragging) return;

      setIsDragging(false);

      // Snap to expanded or collapsed state based on current height
      if (currentHeight > heightOptions.half) {
        setCurrentHeight(heightOptions.expanded);
        setExpandedState("expanded");
      } else if (currentHeight < heightOptions.half) {
        setCurrentHeight(heightOptions.collapsed);
        setExpandedState("collapsed");
      } else {
        setCurrentHeight(heightOptions.half);
        setExpandedState("half");
      }
    };

    if (isDragging) {
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, startY, currentHeight]);

  const handleTouchStart = (e: TouchEvent) => {
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
  };

  const handleDragHandleClick = () => {
    let newState: ActionSheetState;
    if (expandedState === "collapsed") {
      newState = "half";
    } else if (expandedState === "half") {
      newState = "expanded";
    } else {
      newState = "collapsed";
    }
    setExpandedState(newState);
    const newHeight = heightOptions[newState];
    setCurrentHeight(newHeight);
    if (setHeightPercent) {
      setHeightPercent(newHeight);
    }
  };

  return (
    <div
      ref={containerRef}
      className="action-sheet"
      // style={{ height: `${currentHeight}%` }}
      style={{ height: `${heightPercent}%` }}
    >
      <div
        className="action-sheet-handle"
        onTouchStart={(e) => handleTouchStart(e.nativeEvent)}
        onClick={handleDragHandleClick}
      >
        <div className="action-sheet-handle-bar" />
      </div>

      <div className="action-sheet-content">{children}</div>
    </div>
  );
}
